import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import { API_ENDPOINTS } from '../../../../utils/config';
import TaxInvoiceForm from '../../sales/components/TaxInvoiceForm';
import {
  FileText,
  Search,
  AlertCircle,
  CheckCircle,
  Eye,
  Edit,
  Trash2,
  Plus,
  Check,
  X
} from 'lucide-react';

const DebitNotes = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [debitNoteDate, setDebitNoteDate] = useState('');
  const [ordering, setOrdering] = useState('-debit_note_date');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [selected, setSelected] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isTaxFormOpen, setIsTaxFormOpen] = useState(false);

  const [supplierList, setSupplierList] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [createForm, setCreateForm] = useState({
    supplier: '',
    debit_note_date: '',
    due_date: '',
    subtotal: '',
    tax_amount: '',
    total_amount: '',
    status: 'draft',
    type: 'debit_note',
    notes: '',
    terms: '',
    reference_invoice: ''
  });
  const [editForm, setEditForm] = useState({
    supplier: '',
    debit_note_number: '',
    debit_note_date: '',
    due_date: '',
    subtotal: '',
    tax_amount: '',
    total_amount: '',
    status: 'draft',
    type: 'debit_note',
    notes: '',
    terms: '',
    reference_invoice: '',
    items: [{ id: 1, description: '', quantity: 1, rate: 0, amount: 0, tax_rate: 18, tax_amount: 0 }]
  });

  const getErrorMessage = (err, fallback) => {
    const respData = err?.response?.data;
    const message =
      respData?.message ||
      respData?.detail ||
      respData?.error ||
      (typeof respData === 'string' ? respData : null) ||
      err?.message ||
      fallback;
    if (typeof message === 'string') return message;
    try {
      return JSON.stringify(message || fallback);
    } catch {
      return fallback;
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const formatCurrency = (value) =>
    `₹${(parseFloat(value || 0) || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const toMoney2 = (value, defaultValue = '0.00') => {
    const num = Number(value);
    if (Number.isNaN(num)) return defaultValue;
    return num.toFixed(2);
  };

  const exceedsMaxDigits = (moneyString) => {
    if (!moneyString) return false;
    const digitsOnly = moneyString.replace('.', '');
    return digitsOnly.length > 15;
  };

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const taxAmount = items.reduce((sum, item) => sum + (parseFloat(item.tax_amount) || 0), 0);
    return {
      subtotal: toMoney2(subtotal),
      tax_amount: toMoney2(taxAmount),
      total_amount: toMoney2(subtotal + taxAmount)
    };
  };

  const updateItem = (formSetter, index, field, value) => {
    formSetter(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      if (field === 'quantity' || field === 'rate') {
        const quantity = parseFloat(newItems[index].quantity) || 1;
        const rate = parseFloat(newItems[index].rate) || 0;
        newItems[index].amount = toMoney2(quantity * rate);
        const taxRate = parseFloat(newItems[index].tax_rate) || 0;
        newItems[index].tax_amount = toMoney2((quantity * rate * taxRate) / 100);
      } else if (field === 'tax_rate') {
        const quantity = parseFloat(newItems[index].quantity) || 1;
        const rate = parseFloat(newItems[index].rate) || 0;
        const taxRate = parseFloat(value) || 0;
        newItems[index].tax_amount = toMoney2((quantity * rate * taxRate) / 100);
      }
      return { ...prev, items: newItems, ...calculateTotals(newItems) };
    });
  };

  const addItem = (formSetter) => {
    formSetter(prev => ({
      ...prev,
      items: [...prev.items, { id: prev.items.length + 1, description: '', quantity: 1, rate: 0, amount: 0, tax_rate: 18, tax_amount: 0 }]
    }));
  };

  const removeItem = (formSetter, index) => {
    formSetter(prev => {
      const newItems = prev.items.filter((_, i) => i !== index);
      return { ...prev, items: newItems, ...calculateTotals(newItems) };
    });
  };


  // Helper function to get supplier display name
  const getSupplierDisplayName = (supplier) => {
    if (!supplier) return '';
    return supplier.supplier_data?.supplier_name ||
      supplier.supplier_name ||
      supplier.name ||
      supplier.company_name ||
      `Supplier ${supplier.id}`;
  };

  const fetchCompanyProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Accept': 'application/json', 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const response = await axios.get(API_ENDPOINTS.COMPANY_PROFILE_LIST, { headers });
      const profiles = Array.isArray(response.data) ? response.data : (response.data.results || []);
      if (profiles.length > 0) {
        const profile = profiles[0];
        let logoUrl = null;
        if (profile.company_logo || profile.logo) {
          const rawLogo = profile.company_logo || profile.logo;
          if (rawLogo.startsWith('http')) {
            logoUrl = rawLogo;
          } else {
            const baseUrl = (API_ENDPOINTS.API_BASE_URL || '').replace('/api', '');
            logoUrl = rawLogo.startsWith('/') ? `${baseUrl}${rawLogo}` : `${baseUrl}/${rawLogo}`;
          }
        }
        setCompanyProfile({ ...profile, logoUrl });
      }
    } catch {
      // ignore
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.SUPPLIERS_LIST, { headers: getAuthHeaders() });
      const data = res.data;
      const normalized = Array.isArray(data) ? data : data.results || data.data || [];
      setSupplierList(normalized);
      return normalized; // Return the suppliers data
    } catch {
      // Ignore supplier fetch errors to keep UI usable
      return [];
    }
  };

  const fetchItems = async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (debitNoteDate) params.append('debit_note_date', debitNoteDate);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page);
      params.append('ordering', ordering || '-debit_note_date');

      const response = await axios.get(`${API_ENDPOINTS.DEBIT_NOTES_LIST}?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      const data = response.data;
      const normalized = Array.isArray(data) ? data : data.results || data.data || [];
      const normalizedCount = Array.isArray(data) ? data.length : data.count || normalized.length || 0;

      setItems(normalized);
      setTotalCount(normalizedCount);
      setNextPage(Array.isArray(data) ? null : data.next);
      setPrevPage(Array.isArray(data) ? null : data.previous);
      setCurrentPage(page);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch debit notes'));
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setCreateForm((p) => ({ ...p, debit_note_date: today, due_date: today }));
    setEditForm((p) => ({ ...p, debit_note_date: today, due_date: today }));
    fetchSuppliers();
    fetchItems(1);
    fetchCompanyProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchItems(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, ordering, debitNoteDate]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchItems(1), 400);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const openCreate = () => {
    const today = new Date().toISOString().split('T')[0];
    setCreateForm({
      supplier: '',
      debit_note_date: today,
      due_date: today,
      subtotal: '',
      tax_amount: '',
      total_amount: '',
      status: 'draft',
      type: 'debit_note',
      notes: '',
      terms: '',
      reference_invoice: ''
    });
    setIsCreateOpen(true);
    setError('');
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.supplier) {
      setError('Please select a supplier');
      return;
    }
    const subtotalMoney = toMoney2(createForm.subtotal);
    const taxMoney = toMoney2(createForm.tax_amount);
    const totalMoney = toMoney2(createForm.total_amount);
    if (exceedsMaxDigits(subtotalMoney) || exceedsMaxDigits(taxMoney) || exceedsMaxDigits(totalMoney)) {
      setError('Amounts must not exceed 15 total digits.');
      return;
    }
    setIsCreateLoading(true);
    try {
      const payload = {
        supplier: parseInt(createForm.supplier),
        debit_note_date: createForm.debit_note_date || null,
        due_date: createForm.due_date || createForm.debit_note_date || null,
        subtotal: subtotalMoney,
        tax_amount: taxMoney,
        total_amount: totalMoney,
        status: createForm.status,
        type: 'debit_note',
        notes: createForm.notes,
        terms: createForm.terms,
        reference_invoice: createForm.reference_invoice ? parseInt(createForm.reference_invoice) : null
      };
      const response = await axios.post(API_ENDPOINTS.DEBIT_NOTES_CREATE, payload, { headers: getAuthHeaders() });
      const msg = response?.data?.message || 'Debit note created successfully';
      setSuccessMessage(msg);
      setIsCreateOpen(false);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchItems(currentPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create debit note'));
    } finally {
      setIsCreateLoading(false);
    }
  };

  const handleView = async (row) => {
    try {
      setIsLoading(true);
      // Fetch suppliers to get supplier names
      const suppliers = await fetchSuppliers();
      const response = await axios.get(API_ENDPOINTS.DEBIT_NOTES_DETAIL(row.id), { headers: getAuthHeaders() });
      const data = response.data;

      // If supplier is an object, extract the name; if it's an ID, find it in suppliers list
      let supplierName = data.supplier_name || 'N/A';
      if (data.supplier) {
        if (typeof data.supplier === 'object' && data.supplier.id) {
          const supplier = suppliers.find(s => s.id === data.supplier.id);
          supplierName = supplier ? getSupplierDisplayName(supplier) : (data.supplier.supplier_data?.supplier_name || data.supplier.supplier_name || 'N/A');
        } else if (typeof data.supplier === 'number' || typeof data.supplier === 'string') {
          const supplier = suppliers.find(s => s.id === parseInt(data.supplier));
          supplierName = supplier ? getSupplierDisplayName(supplier) : 'N/A';
        }
      }

      // Resolve reference_invoice for display - API may return object or ID
      const refInvoiceDisplay = data.reference_invoice
        ? (typeof data.reference_invoice === 'object' && data.reference_invoice?.invoice_number
          ? data.reference_invoice.invoice_number
          : (data.reference_invoice?.id ?? data.reference_invoice))
        : null;

      setSelected({ ...data, supplier_name: supplierName, reference_invoice_display: refInvoiceDisplay });
      setIsDetailsOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch debit note details'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEdit = async (row) => {
    try {
      setIsEditLoading(true);
      // Ensure suppliers are loaded first and get the data directly
      const suppliers = await fetchSuppliers();
      const response = await axios.get(API_ENDPOINTS.DEBIT_NOTES_DETAIL(row.id), { headers: getAuthHeaders() });
      const data = response.data || row;
      setSelected(data);

      // Extract supplier ID - handle both object and ID cases
      let supplierId = '';
      if (data.supplier) {
        if (typeof data.supplier === 'object' && data.supplier.id) {
          supplierId = data.supplier.id;
        } else if (typeof data.supplier === 'number' || typeof data.supplier === 'string') {
          supplierId = data.supplier;
        }
      }

      // Resolve reference_invoice - API may return object {id, invoice_number} or ID
      let referenceInvoiceVal = '';
      if (data.reference_invoice) {
        if (typeof data.reference_invoice === 'object' && data.reference_invoice.id != null) {
          referenceInvoiceVal = String(data.reference_invoice.id);
        } else if (typeof data.reference_invoice === 'number' || typeof data.reference_invoice === 'string') {
          referenceInvoiceVal = String(data.reference_invoice);
        }
      }

      const rawItems = data.items_detail?.length ? data.items_detail : (data.items?.length ? data.items : []);
      const mappedItems = rawItems.length > 0 ? rawItems.map((item, idx) => ({
        id: idx + 1,
        description: item.description || '',
        quantity: item.quantity || 1,
        rate: item.rate || 0,
        amount: item.amount || 0,
        tax_rate: item.tax_rate || 18,
        tax_amount: item.tax_amount || 0
      })) : [{ id: 1, description: '', quantity: 1, rate: 0, amount: 0, tax_rate: 18, tax_amount: 0 }];

      const totals = calculateTotals(mappedItems);

      setEditForm({
        supplier: supplierId ? String(supplierId) : '',
        debit_note_number: data.debit_note_number || '',
        debit_note_date: data.debit_note_date ? data.debit_note_date.split('T')[0] : '',
        due_date: data.due_date ? data.due_date.split('T')[0] : data.debit_note_date ? data.debit_note_date.split('T')[0] : '',
        subtotal: data.subtotal ?? '',
        tax_amount: data.tax_amount ?? '',
        total_amount: data.total_amount ?? '',
        status: data.status || 'draft',
        type: data.type || 'debit_note',
        notes: data.notes || '',
        terms: data.terms || '',
        reference_invoice: referenceInvoiceVal,
        items: mappedItems,
        ...totals
      });
      setIsEditOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load debit note for editing'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!selected?.id) return;
    if (!editForm.supplier) {
      setError('Please select a supplier');
      return;
    }

    const items = editForm.items || [];
    if (!items.length) {
      setError('Please add at least one item');
      return;
    }
    const subtotalMoney = toMoney2(editForm.subtotal);
    const taxMoney = toMoney2(editForm.tax_amount);
    const totalMoney = toMoney2(editForm.total_amount);
    if (exceedsMaxDigits(subtotalMoney) || exceedsMaxDigits(taxMoney) || exceedsMaxDigits(totalMoney)) {
      setError('Amounts must not exceed 15 total digits.');
      return;
    }
    setIsEditLoading(true);
    try {
      const payload = {
        supplier: parseInt(editForm.supplier),
        debit_note_number: editForm.debit_note_number || null,
        debit_note_date: editForm.debit_note_date || null,
        due_date: editForm.due_date || editForm.debit_note_date || null,
        subtotal: subtotalMoney,
        tax_amount: taxMoney,
        total_amount: totalMoney,
        status: editForm.status,
        type: editForm.type,
        notes: editForm.notes,
        terms: editForm.terms,
        reference_invoice: editForm.reference_invoice ? (parseInt(editForm.reference_invoice) || editForm.reference_invoice) : null,
        items: items.map((item) => ({
          id: item.id,
          item_name: item.item_name,
          description: item.description,
          quantity: parseFloat(item.quantity).toFixed(2),
          rate: parseFloat(item.rate).toFixed(2),
          amount: parseFloat(item.amount).toFixed(2),
          tax_rate: parseFloat(item.tax_rate).toFixed(2),
          tax_amount: parseFloat(item.tax_amount).toFixed(2)
        }))
      };
      const response = await axios.put(
        API_ENDPOINTS.DEBIT_NOTES_UPDATE(selected.id),
        payload,
        { headers: getAuthHeaders() }
      );
      const msg = response?.data?.message || 'Debit note updated successfully';
      setSuccessMessage(msg);
      setIsEditOpen(false);
      setSelected(null);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchItems(currentPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update debit note'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleOpenDelete = (row) => {
    setDeleteTarget(row);
    setIsDeleteOpen(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleteLoading(true);
    try {
      const response = await axios.delete(API_ENDPOINTS.DEBIT_NOTES_DELETE(deleteTarget.id), {
        headers: getAuthHeaders()
      });
      const msg = response?.data?.message || 'Debit note deleted successfully';
      setSuccessMessage(msg);
      setIsDeleteOpen(false);
      setDeleteTarget(null);
      fetchItems(currentPage);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete debit note'));
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const doAction = async (id, endpoint, successFallback) => {
    setIsActionLoading(true);
    try {
      const response = await axios.post(endpoint(id), {}, { headers: getAuthHeaders() });
      const msg = response?.data?.message || response?.data?.status || successFallback;
      setSuccessMessage(msg);
      fetchItems(currentPage);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Action failed'));
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleMarkApproved = (row) =>
    doAction(row.id, API_ENDPOINTS.DEBIT_NOTES_MARK_APPROVED, 'Debit note approved');

  const getStatusBadge = (status) => {
    const base = 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium';
    switch (status) {
      case 'approved':
        return `${base} bg-green-100 text-green-800`;
      case 'sent':
        return `${base} bg-blue-100 text-blue-800`;
      case 'draft':
      default:
        return `${base} bg-gray-100 text-gray-800`;
    }
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Debit Notes</p>
              <p className="text-2xl font-bold text-foreground">{totalCount}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Draft</p>
              <p className="text-2xl font-bold text-foreground">
                {items.filter((q) => q.status === 'draft').length}
              </p>
            </div>
            <div className="p-3 bg-gray-100 rounded-full">
              <FileText className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Sent</p>
              <p className="text-2xl font-bold text-foreground">
                {items.filter((q) => q.status === 'sent').length}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Check className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Approved</p>
              <p className="text-2xl font-bold text-foreground">
                {items.filter((q) => q.status === 'approved').length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search debit notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <input
            type="date"
            value={debitNoteDate}
            onChange={(e) => setDebitNoteDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="approved">Approved</option>
          </select>
          <select
            value={ordering}
            onChange={(e) => setOrdering(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="-debit_note_date">Date: Newest first</option>
            <option value="debit_note_date">Date: Oldest first</option>
            <option value="-total_amount">Amount: High to Low</option>
            <option value="total_amount">Amount: Low to High</option>
          </select>
          <Button onClick={() => setIsTaxFormOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Debit Note
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading debit notes...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-muted-foreground">No debit notes found</p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Debit Note</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center mr-3">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{row.debit_note_number || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{row.supplier_name || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{formatCurrency(row.total_amount)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.debit_note_date ? new Date(row.debit_note_date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.due_date ? new Date(row.due_date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(row.status)}>
                          <span className="capitalize">{row.status || 'N/A'}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-wrap gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleView(row)} title="View">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(row)} title="Edit">
                            <Edit className="w-4 h-4" />
                          </Button>
                          {row.status !== 'approved' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkApproved(row)}
                              title="Mark Approved"
                              disabled={isActionLoading}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleOpenDelete(row)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {(nextPage || prevPage) && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">Showing page {currentPage} of {Math.ceil(totalCount / 10)}</div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => fetchItems(currentPage - 1)} disabled={!prevPage}>
                      Previous
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => fetchItems(currentPage + 1)} disabled={!nextPage}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {/* 
      {isDetailsOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Debit Note Details</h3>
                <p className="text-sm text-muted-foreground">DN #{selected.debit_note_number || 'N/A'}</p>
              </div>
              <Button variant="outline" onClick={() => { setIsDetailsOpen(false); setSelected(null); }}>
                Close
              </Button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><p className="text-muted-foreground">Supplier</p><p className="font-semibold">{selected.supplier_name || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Status</p><p className="font-semibold capitalize">{selected.status || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Type</p><p className="font-semibold capitalize">{selected.type || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Debit Note Date</p><p className="font-semibold">{selected.debit_note_date ? new Date(selected.debit_note_date).toLocaleDateString() : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Due Date</p><p className="font-semibold">{selected.due_date ? new Date(selected.due_date).toLocaleDateString() : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Subtotal</p><p className="font-semibold">{formatCurrency(selected.subtotal)}</p></div>
                <div><p className="text-muted-foreground">Tax Amount</p><p className="font-semibold">{formatCurrency(selected.tax_amount)}</p></div>
                <div><p className="text-muted-foreground">Total Amount</p><p className="font-semibold">{formatCurrency(selected.total_amount)}</p></div>
                <div><p className="text-muted-foreground">Reference Invoice</p><p className="font-semibold">{selected.reference_invoice_display ?? selected.reference_invoice ?? 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Created</p><p className="font-semibold">{selected.created_at ? new Date(selected.created_at).toLocaleString() : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Updated</p><p className="font-semibold">{selected.updated_at ? new Date(selected.updated_at).toLocaleString() : 'N/A'}</p></div>
              </div>

              {selected.notes && (
                <div>
                  <p className="text-sm font-semibold text-foreground">Notes</p>
                  <p className="text-sm text-muted-foreground bg-gray-50 border border-gray-200 rounded p-3">{selected.notes}</p>
                </div>
              )}
              {selected.terms && (
                <div>
                  <p className="text-sm font-semibold text-foreground">Terms</p>
                  <p className="text-sm text-muted-foreground bg-gray-50 border border-gray-200 rounded p-3">{selected.terms}</p>
                </div>
              )}

              {selected.items && selected.items.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2">Items</h4>
                  <div className="overflow-x-auto rounded border border-gray-200">
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Description</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Quantity</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Rate</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Amount</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Tax Rate</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Tax Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.items.map((item, idx) => (
                          <tr key={item.id || idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 border-b text-sm">{item.description || 'N/A'}</td>
                            <td className="px-4 py-2 border-b text-sm">{item.quantity || 'N/A'}</td>
                            <td className="px-4 py-2 border-b text-sm">{formatCurrency(item.rate)}</td>
                            <td className="px-4 py-2 border-b text-sm">{formatCurrency(item.amount)}</td>
                            <td className="px-4 py-2 border-b text-sm">{item.tax_rate ? `${item.tax_rate}%` : 'N/A'}</td>
                            <td className="px-4 py-2 border-b text-sm">{formatCurrency(item.tax_amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isTaxFormOpen && (
        <TaxInvoiceForm
          isOpen={isTaxFormOpen}
          onClose={() => setIsTaxFormOpen(false)}
          onSave={() => { setIsTaxFormOpen(false); fetchItems(currentPage); }}
          invoiceType="debit-note"
        />
      )} */}


      {isDetailsOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Debit Note Details</h3>
              <Button variant="outline" onClick={() => { setIsDetailsOpen(false); setSelected(null); }}>Close</Button>
            </div>
            <div className="p-6 space-y-6">
              {/* Company Header */}
              <div className="border-b-2 border-gray-300 pb-4">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="flex-1 w-full">
                    <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
                      {companyProfile?.company_name || companyProfile?.name || 'COMPANY NAME'}
                    </h1>
                    <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                      <p>
                        Address: {companyProfile?.address || 'Company Address Line 1, City, State, PIN'}
                      </p>
                      <p className="break-words">
                        Mobile: {companyProfile?.phone || '+91 1234567890'} | Email: {companyProfile?.email || 'info@company.com'}
                      </p>
                      <p className="break-words">
                        GSTIN: {companyProfile?.gstin || '29XXXXX1234X1XX'}
                      </p>
                    </div>
                  </div>
                  <div className="text-left sm:text-right w-full sm:w-auto">
                    {companyProfile?.logoUrl ? (
                      <img
                        src={companyProfile.logoUrl}
                        alt={companyProfile.company_name || 'Company Logo'}
                        className="h-12 sm:h-16 object-contain ml-auto"
                      />
                    ) : (
                      <div className="text-base sm:text-lg font-semibold text-primary">LOGO</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Document Type Banner */}
              <div className="bg-foreground text-white text-center py-2 sm:py-3 font-bold text-base sm:text-lg">
                DEBIT NOTE
              </div>

              {/* Invoice Details */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Debit Note No</label>
                  <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm">{selected.debit_note_number || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Debit Note Date</label>
                  <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm">{(selected.debit_note_date || selected.invoice_date) ? new Date(selected.debit_note_date || selected.invoice_date).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm">{selected.due_date ? new Date(selected.due_date).toLocaleDateString() : 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm capitalize">{selected.status || 'N/A'}</p>
                </div>
              </div>

              {/* Supplier Details */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-md font-semibold text-foreground mb-4">Supplier Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                    <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm">{selected.supplier_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <p className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm capitalize">{selected.type?.replace('_', ' ') || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Services Table */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-md font-semibold text-foreground mb-4">Services</h4>
                <div className="overflow-x-auto rounded border border-gray-200">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Description</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Quantity</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Rate</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Amount</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Tax Rate</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Tax Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.items_detail && selected.items_detail.length > 0 ? (
                        selected.items_detail.map((item, idx) => (
                          <tr key={item.id || idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 border-b text-sm">{item.description || 'N/A'}</td>
                            <td className="px-4 py-2 border-b text-sm">{item.quantity || 'N/A'}</td>
                            <td className="px-4 py-2 border-b text-sm">{formatCurrency(item.rate)}</td>
                            <td className="px-4 py-2 border-b text-sm">{formatCurrency(item.amount)}</td>
                            <td className="px-4 py-2 border-b text-sm">{item.tax_rate ? `${item.tax_rate}%` : 'N/A'}</td>
                            <td className="px-4 py-2 border-b text-sm">{formatCurrency(item.tax_amount)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="px-4 py-4 text-center text-sm text-gray-500">No items found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(selected.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Tax Amount:</span>
                      <span>{formatCurrency(selected.tax_amount)}</span>
                    </div>
                    <div className="flex justify-between font-semibold text-base border-t pt-2">
                      <span>Total:</span>
                      <span>{formatCurrency(selected.total_amount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Fields */}

              {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={!!selected.specialise_supply}
                    disabled
                    className="h-4 w-4 text-primary border-gray-300 rounded"
                  />
                  <label className="text-sm text-gray-700">Specialised Supply</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={!!selected.apply_reverse_charge}
                    disabled
                    className="h-4 w-4 text-primary border-gray-300 rounded"
                  />
                  <label className="text-sm text-gray-700">Apply Reverse Charge</label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={!!selected.claim_itc}
                    disabled
                    className="h-4 w-4 text-primary border-gray-300 rounded"
                  />
                  <label className="text-sm text-gray-700">Claim ITC</label>
                </div>
              </div> */}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    rows={3}
                    value={selected.notes || ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Terms</label>
                  <textarea
                    rows={3}
                    value={selected.terms || ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                  />
                </div>
              </div>

              {/* Signature */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-end">
                  <div className="text-center">
                    <div className="border-b border-gray-400 w-48 mb-2"></div>
                    <p className="text-sm text-gray-600">Authorized Signature</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => { setIsDetailsOpen(false); setSelected(null); }}>Close</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isTaxFormOpen && (
        <TaxInvoiceForm
          isOpen={isTaxFormOpen}
          onClose={() => setIsTaxFormOpen(false)}
          onSave={() => { setIsTaxFormOpen(false); fetchItems(currentPage); }}
          invoiceType="debit-note"
        />
      )}

      {/* Create Modal */}

      {isCreateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Create Debit Note</h3>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Close</Button>
            </div>
            <form onSubmit={handleSubmitCreate} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                  <select
                    value={String(createForm.supplier || '')}
                    onChange={(e) => setCreateForm((p) => ({ ...p, supplier: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select supplier</option>
                    {supplierList.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {getSupplierDisplayName(s)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Debit Note Date</label>
                  <input
                    type="date"
                    value={createForm.debit_note_date}
                    onChange={(e) => setCreateForm((p) => ({ ...p, debit_note_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={createForm.due_date}
                    onChange={(e) => setCreateForm((p) => ({ ...p, due_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={createForm.status}
                    onChange={(e) => setCreateForm((p) => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="approved">Approved</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtotal</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createForm.subtotal}
                    onChange={(e) => setCreateForm((p) => ({ ...p, subtotal: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createForm.tax_amount}
                    onChange={(e) => setCreateForm((p) => ({ ...p, tax_amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={createForm.total_amount}
                    onChange={(e) => setCreateForm((p) => ({ ...p, total_amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference Invoice</label>
                  <input
                    type="number"
                    value={createForm.reference_invoice}
                    onChange={(e) => setCreateForm((p) => ({ ...p, reference_invoice: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={createForm.notes}
                  onChange={(e) => setCreateForm((p) => ({ ...p, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Terms</label>
                <textarea
                  value={createForm.terms}
                  onChange={(e) => setCreateForm((p) => ({ ...p, terms: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" type="button" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isCreateLoading}>
                  {isCreateLoading ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Update Debit Note</h3>
              <Button variant="outline" onClick={() => { setIsEditOpen(false); setSelected(null); }}>Close</Button>
            </div>
            <form onSubmit={handleSubmitEdit} className="p-6 space-y-6">
              {/* Company Header */}
              <div className="border-b-2 border-gray-300 pb-4">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="flex-1 w-full">
                    <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
                      {companyProfile?.company_name || companyProfile?.name || 'COMPANY NAME'}
                    </h1>
                    <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                      <p>Address: {companyProfile?.address || 'Company Address Line 1, City, State, PIN'}</p>
                      <p className="break-words">Mobile: {companyProfile?.phone || '+91 1234567890'} | Email: {companyProfile?.email || 'info@company.com'}</p>
                      <p className="break-words">GSTIN: {companyProfile?.gstin || '29XXXXX1234X1XX'}</p>
                    </div>
                  </div>
                  <div className="text-left sm:text-right w-full sm:w-auto">
                    {companyProfile?.logoUrl ? (
                      <img src={companyProfile.logoUrl} alt={companyProfile.company_name || 'Company Logo'} className="h-12 sm:h-16 object-contain ml-auto" />
                    ) : (
                      <div className="text-base sm:text-lg font-semibold text-primary">LOGO</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Document Type Banner */}
              <div className="bg-foreground text-white text-center py-2 sm:py-3 font-bold text-base sm:text-lg">
                DEBIT NOTE
              </div>

              {/* Debit Note Details */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Debit Note No</label>
                  <input
                    type="text"
                    value={editForm.debit_note_number}
                    onChange={(e) => setEditForm((p) => ({ ...p, debit_note_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Debit Note Date</label>
                  <input
                    type="date"
                    value={editForm.debit_note_date}
                    onChange={(e) => setEditForm((p) => ({ ...p, debit_note_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={editForm.due_date}
                    onChange={(e) => setEditForm((p) => ({ ...p, due_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="approved">Approved</option>
                  </select>
                </div>
              </div>

              {/* Supplier Details */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-md font-semibold text-foreground mb-4">Supplier Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                    <select
                      value={String(editForm.supplier || '')}
                      onChange={(e) => setEditForm((p) => ({ ...p, supplier: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Select supplier</option>
                      {supplierList.map((s) => (
                        <option key={s.id} value={String(s.id)}>{getSupplierDisplayName(s)}</option>
                      ))}
                      {selected && editForm.supplier &&
                        !supplierList.find(s => String(s.id) === String(editForm.supplier)) &&
                        selected.supplier_name && (
                          <option value={String(editForm.supplier)}>{selected.supplier_name}</option>
                        )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reference Invoice</label>
                    <input
                      type="text"
                      value={editForm.reference_invoice}
                      onChange={(e) => setEditForm((p) => ({ ...p, reference_invoice: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Services Table */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-md font-semibold text-foreground">Services</h4>
                  <Button type="button" variant="outline" size="sm" onClick={() => addItem(setEditForm)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </div>
                <div className="overflow-x-auto rounded border border-gray-200">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Description</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Quantity</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Rate</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Amount</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Tax Rate</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Tax Amount</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 border-b">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editForm.items.map((item, index) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-2 border-b">
                            <input type="text" value={item.description} onChange={(e) => updateItem(setEditForm, index, 'description', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Item description" />
                          </td>
                          <td className="px-4 py-2 border-b">
                            <input type="number" step="1" value={item.quantity} onChange={(e) => updateItem(setEditForm, index, 'quantity', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                          </td>
                          <td className="px-4 py-2 border-b">
                            <input type="number" step="0.01" value={item.rate} onChange={(e) => updateItem(setEditForm, index, 'rate', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                          </td>
                          <td className="px-4 py-2 border-b text-sm">{formatCurrency(item.amount)}</td>
                          <td className="px-4 py-2 border-b">
                            <input type="number" step="0.01" value={item.tax_rate} onChange={(e) => updateItem(setEditForm, index, 'tax_rate', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                          </td>
                          <td className="px-4 py-2 border-b text-sm">{formatCurrency(item.tax_amount)}</td>
                          <td className="px-4 py-2 border-b">
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(setEditForm, index)} disabled={editForm.items.length === 1}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm"><span>Subtotal:</span><span>{formatCurrency(editForm.subtotal)}</span></div>
                    <div className="flex justify-between text-sm"><span>Tax Amount:</span><span>{formatCurrency(editForm.tax_amount)}</span></div>
                    <div className="flex justify-between font-semibold text-base border-t pt-2"><span>Total:</span><span>{formatCurrency(editForm.total_amount)}</span></div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea rows={3} value={editForm.notes} onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Add notes" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Terms</label>
                  <textarea rows={3} value={editForm.terms} onChange={(e) => setEditForm((p) => ({ ...p, terms: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Add terms" />
                </div>
              </div>

              {/* Signature */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex justify-end">
                  <div className="text-center">
                    <div className="border-b border-gray-400 w-48 mb-2"></div>
                    <p className="text-sm text-gray-600">Authorized Signature</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => { setIsEditOpen(false); setSelected(null); }}>Cancel</Button>
                <Button type="submit" disabled={isEditLoading}>{isEditLoading ? 'Updating...' : 'Update'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Old Edit Modal (commented out)
      {isEditOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Edit Debit Note</h3>
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>Close</Button>
            </div>
            <form onSubmit={handleSubmitEdit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                  <select
                    value={String(editForm.supplier || '')}
                    onChange={(e) => setEditForm((p) => ({ ...p, supplier: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select supplier</option>
                    {supplierList.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {getSupplierDisplayName(s)}
                      </option>
                    ))}
                    {selected && editForm.supplier &&
                      !supplierList.find(s => String(s.id) === String(editForm.supplier)) &&
                      selected.supplier_name && (
                        <option value={String(editForm.supplier)}>
                          {selected.supplier_name}
                        </option>
                      )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Debit Note Number</label>
                  <input
                    type="text"
                    value={editForm.debit_note_number}
                    onChange={(e) => setEditForm((p) => ({ ...p, debit_note_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Debit Note Date</label>
                  <input
                    type="date"
                    value={editForm.debit_note_date}
                    onChange={(e) => setEditForm((p) => ({ ...p, debit_note_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={editForm.due_date}
                    onChange={(e) => setEditForm((p) => ({ ...p, due_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="approved">Approved</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtotal</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.subtotal}
                    onChange={(e) => setEditForm((p) => ({ ...p, subtotal: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.tax_amount}
                    onChange={(e) => setEditForm((p) => ({ ...p, tax_amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.total_amount}
                    onChange={(e) => setEditForm((p) => ({ ...p, total_amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference Invoice</label>
                  <input
                    type="number"
                    value={editForm.reference_invoice}
                    onChange={(e) => setEditForm((p) => ({ ...p, reference_invoice: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Terms</label>
                <textarea
                  value={editForm.terms}
                  onChange={(e) => setEditForm((p) => ({ ...p, terms: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" type="button" onClick={() => setIsEditOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isEditLoading}>
                  {isEditLoading ? 'Updating...' : 'Update'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      */}

      {/* Delete Confirmation */}
      {isDeleteOpen && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-foreground">Delete Debit Note</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Are you sure you want to delete debit note {deleteTarget.debit_note_number || deleteTarget.id}?
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleteLoading}>
                {isDeleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebitNotes;


